import * as React from "react";
import dayjs from "../../lib/dayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { TextField } from "@mui/material";
import { styled } from "@mui/material/styles";

// Styled component to force LTR direction for date inputs in RTL layouts
const LTRTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    direction: 'ltr !important',
    textAlign: 'left !important',
    fontFamily: '"Segoe UI", Roboto, Arial, monospace !important', // Better for date/time display
    fontSize: '14px !important',
  },
  '& .MuiInputBase-root': {
    direction: 'ltr !important',
  },
  // Ensure placeholder is also LTR
  '& .MuiInputBase-input::placeholder': {
    direction: 'ltr !important',
    textAlign: 'left !important',
    opacity: '0.6 !important',
  },
  // Icon positioning
  '& .MuiInputAdornment-root': {
    direction: 'ltr !important',
  }
}));

export function DateTimeField({ 
  value, 
  onChange, 
  min, 
  max, 
  showTimeOnly = false,
  className = "",
  required = false,
  placeholder = "",
  ...props 
}) {
  const [val, setVal] = React.useState(
    value ? dayjs(value) : null
  );

  const minD = min ? dayjs(min) : undefined;
  const maxD = max ? dayjs(max) : undefined;

  React.useEffect(() => {
    setVal(value ? dayjs(value) : null);
  }, [value]);

  if (showTimeOnly) {
    return (
      <TimePicker
        value={val}
        ampm={false}
        minutesStep={5}
        format="HH:mm"  // 24-hour format
        enableAccessibleFieldDOMStructure={false}  // Fix for custom TextField
        onChange={(newVal) => setVal(newVal)}
        onAccept={(finalVal) => {
          if (!finalVal || !finalVal.isValid()) {
            onChange?.({ target: { value: '' } });
            return;
          }
          
          // Preserve existing date but update time
          const currentDate = value ? dayjs(value) : dayjs();
          const updatedDateTime = currentDate
            .hour(finalVal.hour())
            .minute(finalVal.minute())
            .second(0)
            .millisecond(0);
          
          const isoString = updatedDateTime.toDate().toISOString();
          onChange?.({ target: { value: isoString } });
        }}
        slots={{
          textField: LTRTextField
        }}
        slotProps={{
          textField: {
            size: "small",
            fullWidth: true,
            required: required,
            className: `ltr-date-input ${className}`,
            placeholder: placeholder || "HH:mm"
          }
        }}
        {...props}
      />
    );
  }

  return (
    <DateTimePicker
      value={val}
      onChange={(newVal) => setVal(newVal)}
      onAccept={(finalVal) => {
        if (!finalVal || !finalVal.isValid()) {
          onChange?.({ target: { value: '' } });
          return;
        }
        
        // Convert to ISO string for consistent storage
        const isoString = finalVal.toDate().toISOString();
        onChange?.({ target: { value: isoString } });
      }}
      format="DD/MM/YYYY HH:mm"   // guaranteed DD/MM/YYYY format
      ampm={false}                // 24-hour format
      minutesStep={5}             // 5-minute increments
      enableAccessibleFieldDOMStructure={false}  // Fix for custom TextField
      minDateTime={minD}
      maxDateTime={maxD}
      slots={{
        textField: LTRTextField
      }}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          required: required,
          className: `ltr-date-input ${className}`,
          placeholder: placeholder || "DD/MM/YYYY HH:mm"
        }
      }}
      {...props}
    />
  );
}

export default DateTimeField;
